import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { handleBuyClick } from "@/lib/buy";
import ProductCard from "@/components/ProductCard";
import EmptyProducts from "@/components/EmptyProducts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Search } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const category = params.get("category") || "";
  const sort = params.get("sort") || "score";
  const { user } = useAuth();

  useSEO({
    title: category ? `${category} – Sustainable Products | Ecowallet` : "Sustainable Products – Shop by AI Sustainability Score | Ecowallet",
    description: category
      ? `Shop sustainable ${category} products with AI-powered sustainability scores. Compare, find alternatives and earn cashback with Ecowallet.`
      : "Browse 100+ sustainable products across categories. Every product has a transparent AI Sustainability Score, cashback and Green Points on Ecowallet.",
    keywords: "sustainable products online, sustainable product finder, sustainable products, sustainable shopping app, eco friendly products",
  });

  useEffect(() => {
    (async () => {
      const [pRes, cRes] = await Promise.all([
        api.get("/products", { params: { category: category || undefined, sort, q: q || undefined, limit: 100 } }),
        api.get("/products/categories"),
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
    })();
  }, [category, sort, q]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const handleBuy = (product) => handleBuyClick(product, { user });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[{ label: "Products", to: category ? "/products" : undefined }, ...(category ? [{ label: category }] : [])]} />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 my-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">{category || "All Sustainable Products"}</h1>
          <p className="text-muted-foreground mt-2">Shop {products.length} {category ? category.toLowerCase() : "sustainable"} products with real AI Sustainability Scores.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search products..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-full border border-border bg-card text-sm w-56 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="search-input"
            />
          </div>
          <Select value={category || "all"} onValueChange={(v) => update("category", v)}>
            <SelectTrigger className="w-52 rounded-full" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => update("sort", v)}>
            <SelectTrigger className="w-44 rounded-full" data-testid="filter-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="score">Most Sustainable</SelectItem>
              <SelectItem value="cashback">Highest Cashback</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
        {products.map((p) => <ProductCard key={p.id} product={p} onBuy={handleBuy} />)}
      </div>
      {products.length === 0 && <EmptyProducts />}
    </div>
  );
};

export default ProductsPage;
